const MEMBER_QR_ISSUER = '6am-bkk';
const MEMBER_QR_AUDIENCE = '6am-member-qr';
// v3 dropped expiry. Bumping the version means v2 tokens, which carried an
// `exp`, no longer verify — they cannot be replayed against the new rules.
const MEMBER_QR_VERSION = 3;

type MemberQrJwtHeader = {
  alg: 'HS256';
  typ: 'JWT';
};

/**
 * A member's code says who they are and nothing else. There is deliberately no
 * `exp`, no `iat` and no `jti`: the claims are a pure function of the member, so
 * the same person always produces the exact same code. It is the paper race bib
 * of the app — it identifies, it does not authenticate, and it is only ever
 * redeemed by an already-signed-in operator.
 */
type MemberQrJwtClaims = {
  aud: typeof MEMBER_QR_AUDIENCE;
  iss: typeof MEMBER_QR_ISSUER;
  op?: MemberQrOperation;
  sub: string;
  v: typeof MEMBER_QR_VERSION;
};

type MemberQrOperation = 'identify_member' | 'add_friend' | 'event_checkin';

const MEMBER_QR_OPERATIONS: readonly MemberQrOperation[] = [
  'identify_member',
  'add_friend',
  'event_checkin',
];

type MemberQrJwtInput = {
  operation?: MemberQrOperation | null;
  userId: string;
};

type ParsedMemberQrJwt =
  | {
      claims: MemberQrJwtClaims;
      isValid: true;
      jwt: string;
      userId: string;
    }
  | {
      error: string;
      isValid: false;
      jwt: string;
    };

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

function stringToUtf8Bytes(value: string) {
  const encoded = encodeURIComponent(value);
  const bytes: number[] = [];

  for (let index = 0; index < encoded.length; index += 1) {
    if (encoded[index] === '%') {
      bytes.push(Number.parseInt(encoded.slice(index + 1, index + 3), 16));
      index += 2;
    } else {
      bytes.push(encoded.charCodeAt(index));
    }
  }

  return bytes;
}

function utf8BytesToString(bytes: number[]) {
  return decodeURIComponent(bytes.map((byte) => `%${byte.toString(16).padStart(2, '0')}`).join(''));
}

function encodeBase64Url(value: string) {
  const bytes = stringToUtf8Bytes(value);
  let encoded = '';

  for (let index = 0; index < bytes.length; index += 3) {
    const first = bytes[index];
    const second = bytes[index + 1];
    const third = bytes[index + 2];
    const hasSecond = index + 1 < bytes.length;
    const hasThird = index + 2 < bytes.length;

    encoded += BASE64_ALPHABET[first >> 2];
    encoded += BASE64_ALPHABET[((first & 3) << 4) | ((second ?? 0) >> 4)];
    encoded += hasSecond ? BASE64_ALPHABET[((second & 15) << 2) | ((third ?? 0) >> 6)] : '=';
    encoded += hasThird ? BASE64_ALPHABET[third & 63] : '=';
  }

  return encoded.replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
}

function decodeBase64Url(value: string) {
  const normalized = value.replaceAll('-', '+').replaceAll('_', '/');
  const bytes: number[] = [];

  for (let index = 0; index < normalized.length; index += 4) {
    const first = BASE64_ALPHABET.indexOf(normalized[index]);
    const second = BASE64_ALPHABET.indexOf(normalized[index + 1]);
    const third = BASE64_ALPHABET.indexOf(normalized[index + 2]);
    const fourth = BASE64_ALPHABET.indexOf(normalized[index + 3]);

    bytes.push((first << 2) | (second >> 4));

    if (third >= 0) {
      bytes.push(((second & 15) << 4) | (third >> 2));
    }

    if (fourth >= 0) {
      bytes.push(((third & 3) << 6) | fourth);
    }
  }

  return utf8BytesToString(bytes);
}

function parseMemberQrJwt(jwt: string): ParsedMemberQrJwt {
  const token = jwt.trim();
  const parts = token.split('.');

  if (parts.length !== 3) {
    return { error: 'QR code is not a JWT.', isValid: false, jwt: token };
  }

  try {
    const header = JSON.parse(decodeBase64Url(parts[0])) as Partial<MemberQrJwtHeader>;
    const claims = JSON.parse(decodeBase64Url(parts[1])) as Partial<MemberQrJwtClaims>;

    if (header.typ !== 'JWT' || header.alg !== 'HS256' || !parts[2]) {
      return { error: 'QR code token is missing a JWT header.', isValid: false, jwt: token };
    }

    if (
      claims.iss !== MEMBER_QR_ISSUER ||
      claims.aud !== MEMBER_QR_AUDIENCE ||
      claims.v !== MEMBER_QR_VERSION ||
      typeof claims.sub !== 'string' ||
      (claims.op != null && !MEMBER_QR_OPERATIONS.includes(claims.op))
    ) {
      return { error: 'QR code is not a 6am member token.', isValid: false, jwt: token };
    }

    return {
      claims: claims as MemberQrJwtClaims,
      isValid: true,
      jwt: token,
      userId: claims.sub,
    };
  } catch {
    return { error: 'QR code token could not be decoded.', isValid: false, jwt: token };
  }
}

export {
  MEMBER_QR_AUDIENCE,
  MEMBER_QR_ISSUER,
  MEMBER_QR_VERSION,
  decodeBase64Url,
  encodeBase64Url,
  parseMemberQrJwt,
};
export type {
  MemberQrJwtClaims,
  MemberQrJwtHeader,
  MemberQrJwtInput,
  MemberQrOperation,
  ParsedMemberQrJwt,
};
